import type { FocusEvent, KeyboardEvent, MouseEvent, RefObject } from 'react';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useTopNavTracking } from '~/utilities/hooks/useTopNavTracking';

type AccountMenuInteraction = {
  isOpen: boolean;
  expandedItems: Record<string, boolean>;
  menuRef: RefObject<HTMLDivElement | null>;
  toggleButtonRef: RefObject<HTMLButtonElement | null>;
  iconLinkRef: RefObject<HTMLAnchorElement | null>;
  closeMenu: () => void;
  toggleItemExpanded: (itemKey: string) => void;
  handleIconMouseEnter: () => void;
  handleIconMouseMove: () => void;
  handleMouseEnter: (event?: MouseEvent<HTMLDivElement>) => void;
  handleMouseMove: (event?: MouseEvent<HTMLDivElement>) => void;
  handleMouseLeave: () => void;
  handleContainerFocus: () => void;
  handleContainerMouseDown: () => void;
  handleLinkFocus: () => void;
  handleToggleClick: (event?: MouseEvent<HTMLButtonElement>) => void;
  handleDropdownBlur: (event: FocusEvent<HTMLDivElement>) => void;
  handleContainerBlur: (event: FocusEvent<HTMLDivElement>) => void;
  handleToggleKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  handleLinkKeyDown: (event: KeyboardEvent<HTMLAnchorElement>) => void;
  handleContainerKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
};

export const useAccountMenuInteraction = (): AccountMenuInteraction => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const iconLinkRef = useRef<HTMLAnchorElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isKeyboardUserRef = useRef<boolean>(false);
  const openedByHoverTimeRef = useRef<number>(0);
  const isOpenRef = useRef<boolean>(false);
  const { track } = useTopNavTracking();

  const getFirstFocusableElement = useCallback(
    () =>
      menuRef.current?.querySelector<HTMLElement>(
        '#desktop-account-menu-dropdown a[href], #desktop-account-menu-dropdown button:not([disabled])'
      ),
    []
  );

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    if (!isOpenRef.current) {
      track({ event: 'header_interaction', element: 'account', interaction_type: 'open' });
    }

    isOpenRef.current = true;
    setIsOpen(true);
  }, [track]);

  const closeMenu = useCallback(() => {
    openedByHoverTimeRef.current = 0;
    isOpenRef.current = false;
    setIsOpen(false);
  }, []);

  const handleIconMouseEnter = useCallback(() => {
    isKeyboardUserRef.current = false;

    clearCloseTimeout();
    openedByHoverTimeRef.current = Date.now();
    openMenu();
  }, [clearCloseTimeout, openMenu]);

  const handleIconMouseMove = useCallback(() => {
    if (isOpen) {
      return;
    }

    handleIconMouseEnter();
  }, [handleIconMouseEnter, isOpen]);

  const handleMouseEnter = useCallback(
    (event?: MouseEvent<HTMLDivElement>) => {
      if (
        isKeyboardUserRef.current ||
        (toggleButtonRef.current &&
          event?.target &&
          toggleButtonRef.current.contains(event.target as Node))
      ) {
        return;
      }

      clearCloseTimeout();
      openedByHoverTimeRef.current = Date.now();
      openMenu();
    },
    [clearCloseTimeout, openMenu]
  );

  const handleMouseMove = useCallback(
    (event?: MouseEvent<HTMLDivElement>) => {
      if (
        isOpen ||
        isKeyboardUserRef.current ||
        (toggleButtonRef.current &&
          event?.target &&
          toggleButtonRef.current.contains(event.target as Node))
      ) {
        return;
      }

      clearCloseTimeout();
      openedByHoverTimeRef.current = Date.now();
      openMenu();
    },
    [clearCloseTimeout, isOpen, openMenu]
  );

  const handleMouseLeave = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(closeMenu, 150);
  }, [clearCloseTimeout, closeMenu]);

  const handleContainerFocus = useCallback(() => {
    isKeyboardUserRef.current = true;
  }, []);

  const handleContainerMouseDown = useCallback(() => {
    isKeyboardUserRef.current = false;
  }, []);

  const handleLinkFocus = useCallback(() => {
    isKeyboardUserRef.current = true;
    closeMenu();
  }, [closeMenu]);

  const handleToggleClick = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      const isPendingClose = Boolean(closeTimeoutRef.current);

      clearCloseTimeout();

      // If the menu was opened by hover within the last 350ms and pointer is still
      // inside the menu (no pending close timeout), keep it open rather than toggling closed.
      // Note: Do not rely on `isOpen` state here because React 18 state batching may not have
      // committed the render from handleMouseEnter before the click event fires.
      if (!isPendingClose && Date.now() - openedByHoverTimeRef.current < 350) {
        openMenu();

        return;
      }

      const isKeyboardTrigger = event && event.detail === 0;

      if (!isKeyboardTrigger) {
        isKeyboardUserRef.current = false;
      }

      if (isOpenRef.current) {
        closeMenu();

        if (!isKeyboardTrigger) {
          toggleButtonRef.current?.blur();
        }

        return;
      }

      openMenu();
    },
    [clearCloseTimeout, closeMenu, openMenu]
  );

  const handleDropdownBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        closeMenu();
      }
    },
    [closeMenu]
  );

  const handleContainerBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        closeMenu();
      }
    },
    [closeMenu]
  );

  const handleToggleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Tab' && !event.shiftKey && isOpen) {
        const firstDropdownLink = getFirstFocusableElement();

        if (firstDropdownLink) {
          event.preventDefault();
          firstDropdownLink.focus();
        }
      }

      if (event.key === 'Tab' && event.shiftKey && isOpen) {
        closeMenu();
      }
    },
    [closeMenu, getFirstFocusableElement, isOpen]
  );

  const toggleItemExpanded = useCallback((itemKey: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  }, []);

  const handleLinkKeyDown = useCallback((event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      iconLinkRef.current?.click();
    }
  }, []);

  const handleContainerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      isKeyboardUserRef.current = true;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        toggleButtonRef.current?.focus();
      }
    },
    [closeMenu]
  );

  useEffect(() => {
    const handleGlobalKeyDown = (event: globalThis.KeyboardEvent) => {
      if (
        event.key === 'Tab' ||
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight'
      ) {
        isKeyboardUserRef.current = true;
      }
    };

    const handleGlobalMouseDown = () => {
      isKeyboardUserRef.current = false;
    };

    window.addEventListener('keydown', handleGlobalKeyDown, true);
    window.addEventListener('mousedown', handleGlobalMouseDown, true);

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, true);
      window.removeEventListener('mousedown', handleGlobalMouseDown, true);
    };
  }, []);

  useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  useEffect(() => {
    if (isOpen) {
      const handleDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMenu();

          if (menuRef.current?.contains(document.activeElement)) {
            toggleButtonRef.current?.focus();
          }
        }
      };

      const handleClickOutside = (event: globalThis.MouseEvent | TouchEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          closeMenu();
        }
      };

      document.addEventListener('keydown', handleDocumentKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);

      return () => {
        document.removeEventListener('keydown', handleDocumentKeyDown);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }

    return undefined;
  }, [closeMenu, isOpen]);

  return {
    isOpen,
    expandedItems,
    menuRef,
    toggleButtonRef,
    iconLinkRef,
    closeMenu,
    toggleItemExpanded,
    handleIconMouseEnter,
    handleIconMouseMove,
    handleMouseEnter,
    handleMouseMove,
    handleMouseLeave,
    handleContainerFocus,
    handleContainerMouseDown,
    handleLinkFocus,
    handleToggleClick,
    handleDropdownBlur,
    handleContainerBlur,
    handleToggleKeyDown,
    handleLinkKeyDown,
    handleContainerKeyDown,
  };
};
